import React, { useEffect, useState } from "react"
import ClientReportCard from "../../components/ClientReportCard.molecule"
import { getClientSprintBudgetStateBySprintId } from "../../../../services/clientReport.service"
import useReportFiltersContext from "../../../../hooks/contextConsumers/useReportFiltersContext.hook"
import NavigateToBudgetButton from "../../components/NavigateToBudgetButton.molecule"
import {
  getEstimatedBudgetEnd,
  isBudgetTypeCredit,
} from "../../../../utils/budget.utils"
import { DateTime } from "luxon"
import {
  budgetTypeCreditTemplate,
  budgetTypeDebitTemplate,
} from "../../components/ClientBudgetCardTemplate.molecule"

export const GENERAL_SPRINT_PROGRESS_CARD_MODES = {
  COMPACT: "compact",
  LONG: "long",
}

const ClientSprintBudgetState = ({
  refresh,
  estimatedPointsFinishDate,
  sprintData = {},
  sizeMode = GENERAL_SPRINT_PROGRESS_CARD_MODES.COMPACT,
  refChart,
  statusVisible = true,
  isPresentationMode = false,
  collapsible = false,
  extraData = {},
}) => {
  const [data, setData] = useState({})
  const [loading, setLoading] = useState(false)
  const { clientReportSelectedClient, isClientReportSelectedClientBillable } =
    useReportFiltersContext()
  const estimatedEndDate =
    isPresentationMode ? sprintData?.estimatedEndDate || null : null
  const sprintId =
    isPresentationMode ?
      sprintData?.sprintId
    : (extraData?.sprintId ?? sprintData?.id)

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal
    fetchSprintBudgetState(signal)
    return () => {
      controller.abort()
    }
  }, [sprintId, refresh, estimatedPointsFinishDate])

  const fetchSprintBudgetState = async (signal) => {
    setLoading(true)
    try {
      if (!sprintId) {
        setData({})
        return setLoading(false)
      }

      const response = await getClientSprintBudgetStateBySprintId(sprintId, {
        signal,
      })

      if (!response.success) {
        if (signal?.aborted) return
        throw new Error(response.message)
      }
      const result = response.result
      if (!result) {
        setData({})
        return setLoading(false)
      }
      if (estimatedPointsFinishDate) {
        const avg = Number(result?.lastEightWeeksTruncatedMean)
        if (!avg || avg <= 0) {
          result.remainingWeeks = null
        } else {
          const monthlyConsumption = avg * 4

          const estimatedBudgetEnd = getEstimatedBudgetEnd(
            result?.totalBudget - result?.totalBilledTime,
            monthlyConsumption,
            monthlyConsumption
          )?.estimatedEndMaxDate

          if (!estimatedBudgetEnd) {
            result.remainingWeeks = null
          } else {
            const pointsDate = DateTime.fromFormat(
              estimatedPointsFinishDate,
              "dd/MM/yyyy"
            )

            let remainingWeeks = estimatedBudgetEnd.diff(
              pointsDate,
              "weeks"
            )?.weeks

            if (!Number.isFinite(remainingWeeks)) {
              remainingWeeks = null
            } else if (remainingWeeks < 0) {
              remainingWeeks = 0
            }

            result.remainingWeeks = remainingWeeks
          }
        }
      }

      setData(result)
    } catch (error) {
      console.error("Error fetching sprint progress data:", error)
    } finally {
      setLoading(false)
    }
  }
  const hasPrediction =
    data?.remainingWeeks != null && Number.isFinite(data?.remainingWeeks)
  const normalizedRemainingWeeks =
    hasPrediction ? data.remainingWeeks : undefined
  return (
    <ClientReportCard
      title={"Estado del presupuesto"}
      headerButton={
        <NavigateToBudgetButton
          clientId={clientReportSelectedClient}
          isClientBillable={isClientReportSelectedClientBillable}
          loading={loading}
        />
      }
      iconClassName="pi pi-wallet text-3xl"
      className="font-cairo flex-shrink-0"
      collapsible={collapsible}
    >
      {isBudgetTypeCredit(data?.clientBudgetTypeId) ?
        budgetTypeCreditTemplate({
          suggestedMonthlyBudgetLimit: data?.suggestedMonthlyBudgetLimit,
          lastMonthBilledTime: data?.lastMonthBilledTime,
          estimatedEndDate: DateTime.now()
            .plus({ months: 1 })
            .startOf("month")
            .toFormat("dd/MM/yyyy"),
          loading,
          sizeMode,
          refChart,
        })
      : budgetTypeDebitTemplate({
          totalBudget: data?.totalBudget,
          historicalBudget: data?.historicalBudget,
          totalBilledTime:
            isPresentationMode ?
              sprintData?.accumulatedPeriodConsumption
            : (extraData?.accumulatedPeriodConsumption ??
              data?.totalBilledTime),
          hasBudget: data?.hasBudget,
          lastEightWeeksTruncatedMean: data?.lastEightWeeksTruncatedMean,
          remainingWeeks: normalizedRemainingWeeks,
          estimatedEndDate,
          isPresentationMode,
          loading,
          sizeMode,
          refChart,
          statusVisible,
          extraData,
        })
      }
    </ClientReportCard>
  )
}

export default ClientSprintBudgetState
